/**
 * Label printer setup belonging to THIS phone, not to the user or the
 * worksheet: which roll is loaded, how many stickers a bundle gets, and whether
 * saving an entry should print one automatically.
 *
 * Each station is set up once — the phone next to the 90x50 printer keeps 4
 * copies, the one next to the 100x150 printer keeps 1 — so this has to be
 * per-device storage. The batch print page and auto-print read the same record,
 * or the sticker reprinted later would not match the one already on the goods.
 */

export const DEVICE_CONFIG_KEY = "labelPrintDeviceConfig";

export const DEVICE_CONFIG_FIELDS = [
  "labelWidth",
  "labelHeight",
  "rotate",
  "left",
  "right",
  "top",
  "bottom",
  "qrSize",
  "copies",
  "fontSize",
  "headerFontSize",
  "footerFontSize",
];

// The two rolls in use. Offered as named choices rather than free width/height
// boxes: a mistyped dimension prints a whole run at the wrong size, and there is
// no way to tell from the phone that it happened.
export const LABEL_SIZES = [
  { key: "90x50", title: "90 × 50 mm", labelWidth: 90, labelHeight: 50 },
  { key: "100x150", title: "100 × 150 mm", labelWidth: 150, labelHeight: 100 },
];

export function sizeKeyOf(parameters) {
  const w = Number(parameters.labelWidth);
  const h = Number(parameters.labelHeight);
  const match = LABEL_SIZES.find(
    (s) => s.labelWidth === w && s.labelHeight === h
  );
  return match ? match.key : "";
}

export function loadDeviceConfig() {
  try {
    const raw = localStorage.getItem(DEVICE_CONFIG_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    // stale field from the removed 3-up feature — ignore if present
    delete parsed.labelsPerPage;
    return parsed;
  } catch (error) {
    return null;
  }
}

export function saveDeviceConfig(parameters, extra = {}) {
  const config = loadDeviceConfig() || {};
  DEVICE_CONFIG_FIELDS.forEach((key) => {
    if (parameters[key] !== undefined) {
      config[key] = parameters[key];
    }
  });
  Object.assign(config, extra);
  try {
    localStorage.setItem(DEVICE_CONFIG_KEY, JSON.stringify(config));
  } catch (error) {
    // Storage unavailable — the setup just won't survive a reload.
  }
  return config;
}

// Off unless this phone was deliberately set up to print. Most stations have no
// printer next to them, and a phone that prints unasked wastes a roll.
export function isAutoPrintEnabled() {
  const config = loadDeviceConfig();
  return !!(config && config.autoPrint);
}

export function setAutoPrintEnabled(enabled) {
  const config = loadDeviceConfig() || {};
  config.autoPrint = !!enabled;
  try {
    localStorage.setItem(DEVICE_CONFIG_KEY, JSON.stringify(config));
  } catch (error) {
    // ignore
  }
}

// The print head is about 100mm wide (the AE240-family spec lists 40-110mm
// paper). A layout wider than that cannot be fed as-is: the media goes through
// with its short edge across the head and the image is rotated a quarter turn
// to match. That is how 100x150 prints as a bigger version of the same
// landscape sticker rather than a squashed portrait one.
export const MAX_HEAD_WIDTH_MM = 110;

export function needsRotate90(parameters) {
  return (Number(parameters.labelWidth) || 0) > MAX_HEAD_WIDTH_MM;
}

// Print darkness (0-15) and feed speed, tunable on the spot without a redeploy:
// ?density=12&speed=2 on the label page, remembered on that phone afterwards.
// A faint leading edge means the head is too cold or the paper is moving too
// fast for it, and the right value depends on the roll as much as the printer.
// Chosen on the floor: dark enough on the stock in use, without the heat spread
// that closes up the QR's white gaps and makes it harder to scan rather than
// easier. Note this REPLACES whatever the printer is set to rather than raising
// it, so a unit already configured above 10 will print lighter — set that
// station with ?density= if one ever turns up.
const DEFAULT_DENSITY = 10;

export function printQuality() {
  const read = (name, fallback, min, max) => {
    try {
      const hashQuery = window.location.hash.split("?")[1] || "";
      const q =
        new URLSearchParams(hashQuery).get(name) ||
        new URLSearchParams(window.location.search).get(name);
      if (q !== null && q !== "" && Number(q) >= min && Number(q) <= max) {
        localStorage.setItem(`labelPrint_${name}`, String(Number(q)));
        return Number(q);
      }
      // Check for the key's ABSENCE before converting. localStorage returns
      // null when nothing is stored, Number(null) is 0, and 0 is inside the
      // valid range for density — so every device that had never set one
      // silently sent DENSITY 0 and printed grey, with the default below never
      // reached. It looked like the printer was at zero; it was this.
      const raw = localStorage.getItem(`labelPrint_${name}`);
      if (raw !== null && raw !== "") {
        const saved = Number(raw);
        if (!Number.isNaN(saved) && saved >= min && saved <= max) {
          return saved;
        }
      }
    } catch (e) {
      // fall through
    }
    return fallback;
  };
  // The AS240 ships with density 0 — the minimum — which is why labels printed
  // grey no matter what else was changed. 10 is comfortably into the usable
  // range without bleeding the QR's white gaps shut. Speed is still left alone
  // unless someone asks for it.
  // These printers ship with darkness at 0 and print grey until it is raised.
  // Verified on the floor: the AS240 accepts DENSITY, and 10 prints properly.
  // Sending it means a new printer works out of the box rather than needing
  // someone to find its darkness setting before it is usable.
  //
  // It overrides whatever the printer is set to, which cut both ways: on a unit
  // already set above 10 this made every label FAINTER. If a printer arrives
  // configured, set this per device with ?density= rather than assuming 10.
  const out = { density: read("density", DEFAULT_DENSITY, 0, 15) };
  const sp = read("speed", null, 1, 6);
  if (sp != null) {
    out.speed = sp;
  }
  return out;
}

// How much bigger this label is than the 90x50 baseline. Shared so the sticker
// and the QR generator cannot disagree about it.
export function labelSizeScale(parameters) {
  const w = Number(parameters.labelWidth) || 90;
  const h = Number(parameters.labelHeight) || 50;
  const scale = Math.min(w / 90, h / 50);
  return scale > 0 ? scale : 1;
}

// NOTE: generating the QR at its exact printed dot count was tried and taken
// back out. It was introduced while chasing print quality that turned out to be
// a dirty print head, so it never had a fair test, and it is not worth carrying
// an unproven change to the one element that has to scan. The QR is generated
// large and scaled down, which is what has always printed legibly here.

// Blank paper before the content on a rotated label. The head is cold at the
// leading edge and prints faint there, and with auto-print that is EVERY label
// rather than the first of a run — so the strip has to be wide enough that the
// fade is over before the QR begins. 6mm was not; how far it runs depends on
// the roll and how long the printer has been sitting, so it is tunable with
// ?lead=16 rather than being another constant to redeploy.
export function leadingMarginMm() {
  try {
    const hashQuery = window.location.hash.split("?")[1] || "";
    const q =
      new URLSearchParams(hashQuery).get("lead") ||
      new URLSearchParams(window.location.search).get("lead");
    if (q !== null && q !== "" && Number(q) >= 0 && Number(q) <= 40) {
      localStorage.setItem("labelPrintLeadMm", String(Number(q)));
      return Number(q);
    }
    const saved = Number(localStorage.getItem("labelPrintLeadMm"));
    if (saved > 0 && saved <= 40) {
      return saved;
    }
  } catch (e) {
    // fall through
  }
  return 12;
}
